import { Table, TableBody, TableCell, TableRow } from 'unbrace/components';
import { ChevronDown, ChevronUp } from 'components/icons';
import TableWrapper from 'components/Table/TableWrapper';
import { sortDateStrings, SortOrder } from 'helpers';
import { useDisclosure } from 'hooks';
import { MultiSelectActions } from 'hooks/useMultiSelect';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from '../../../../config/theme';
import { ChangedPrices } from '../../containers/ProductDefaultView';
import { Pricing } from '../../types/pricing';
import ProductPricingRow from './ProductPricingRow';

type Props = {
  changedPrices: ChangedPrices;
  handleDelete: (price: Pricing) => void;
  handleFormChange: (values: Pricing) => void;
  isEntity?: boolean;
  pricing: Pricing[];
  selectedPricing: Pricing[];
  selectedPricingActions: MultiSelectActions;
};

const ProductPricingGroup: React.FC<Props> = ({
  changedPrices,
  handleDelete,
  handleFormChange,
  isEntity,
  pricing,
  selectedPricing,
  selectedPricingActions,
}) => {
  const { t } = useTranslation('products');
  const { isOpen: showInactive, toggle: toggleShowInactive } = useDisclosure();
  const [first] = pricing;

  const sortedPrice = pricing
    .toSorted((pr1, pr2) => sortDateStrings(pr1.dateFrom, pr2.dateFrom, SortOrder.DESCEND))
    // sort for M/M, Y/M, Y/Y
    .toSorted((pr1, pr2) =>
      `${pr1.termDurationId}${pr1.billingPlanId}` < `${pr2.termDurationId}${pr2.billingPlanId}` ? 1 : -1
    );

  const groupedByDescription = Object.groupBy(sortedPrice, (pr: Pricing) => pr.description || '');

  return (
    <React.Fragment>
      <PricingGroup>
        <h1>{isEntity ? `${t('pricing.pricingPerEntityTitle')} ${first.entityName}` : t('pricing.pricingTitle')}</h1>
        {Object.entries(groupedByDescription).map(([priceType, pricings]) => {
          const groupedByActive = Object.groupBy(pricings || [], (pr: Pricing) =>
            (pr.dateUntil && new Date(pr.dateUntil) > new Date()) || !pr.dateUntil ? 'active' : 'inactive'
          );

          return (
            <React.Fragment key={priceType}>
              <h2>{priceType}</h2>
              <TableWrapper key={priceType} style={{ marginBottom: '25px' }}>
                <Table>
                  <TableBody>
                    {groupedByActive.active?.map((price: Pricing) => (
                      <ProductPricingRow
                        handleDelete={handleDelete}
                        handleFormChange={handleFormChange}
                        key={price.priceId}
                        price={(price.priceId && changedPrices[price.priceId]) || price}
                        selectedPricing={selectedPricing}
                        selectedPricingActions={selectedPricingActions}
                      />
                    ))}
                    {groupedByActive.inactive?.length && (
                      <>
                        <TableRow>
                          <TableCell colSpan={5} onClick={toggleShowInactive} style={{ padding: 0 }}>
                            <StyledShowMoreRow>
                              {showInactive ? <ChevronUp /> : <ChevronDown />}
                              <h3>{showInactive ? t('pricing.hideInactive') : t('pricing.showInactive')}</h3>
                            </StyledShowMoreRow>
                          </TableCell>
                        </TableRow>
                        {showInactive &&
                          groupedByActive.inactive?.map((price: Pricing) => (
                            <ProductPricingRow
                              handleDelete={handleDelete}
                              handleFormChange={handleFormChange}
                              key={price.priceId}
                              price={(price.priceId && changedPrices[price.priceId]) || price}
                              selectedPricing={selectedPricing}
                              selectedPricingActions={selectedPricingActions}
                            />
                          ))}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableWrapper>
            </React.Fragment>
          );
        })}
      </PricingGroup>
    </React.Fragment>
  );
};

const PricingGroup = styled.article`
  padding: 40px;
  > h1 {
    font-size: ${(props) => props.theme.product.pricing.fontSize.title};
    font-weight: ${(props) => props.theme.product.pricing.fontWeight.title};
    margin: 0 0 15px 0;
  }
`;

const StyledShowMoreRow = styled.div`
  display: flex;
  padding: 10px 0px 10px 15px;
  align-items: center;
  align-content: center;
  gap: 4;
  :hover {
    cursor: pointer;
  }
`;

export default ProductPricingGroup;
